import { EnvironmentProviders, InjectionToken, NgModule, Provider, Type } from '@angular/core';
import { CommonModule, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { MetaReducer, StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { storeFreeze } from 'ngrx-store-freeze';
import { BrowserModule, provideClientHydration, withIncrementalHydration } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { CookieService } from 'ngx-cookie-service';
import { provideIndexedDb } from 'ngx-indexed-db';

import '@extend-types';
import { AppRoutingModule } from '@app/app-routing.module';
import { AppContainer } from '@app/app.container';
import { CoreModule } from '@app/modules/core/core.module';
import { environment as ENV } from '@environment';
import { globalReducers } from '@app/store/reducers';
import { BootstrapEffects } from '@app/store/effects/bootstrap.effects';
import { AppFacade } from '@app/app.facade';
import { RouterEffect } from '@app/store/effects/router.effects';
import { ErrorInterceptoService } from '@app/services/error-interceptor.service';
import { ToastrEffect } from '@app/store/effects/toastr.effects';
import { ModuleCountryGuard } from '@app/services/guard/module-country.guard';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { getLocale } from '@app/constants/locales.constanst';
import { LocalStorageEffect } from '@app/store/effects/local-storage.effects';
import { LocalStorageService } from '@app/services/local-storage.service';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { NotificationsModule } from '@notifications/notifications.module';
import { FrequentFlyerProgramsService } from '@app/services/frequent-flyer-programs.service';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { ProvincesAndCitiesExternalService } from '@app/services/provinces-cities-external.service';
import { SessionStorageService } from '@app/services/session-storage.service';
import { StatsigModule } from '@statsig/statsig.module';
import { TravelToastModule } from '@ux/components/travel-toast/travel-toast.module';
import { TravelToastComponent } from '@ux/components/travel-toast/travel-toast.component';
import { AvoService } from '@avo/services/avo.service';
import { MaintenancePageModule } from '@app/modules/maintenance-page/maintenance-page.module';
import { ModalPrimeModule } from '@app/modules/modal-prime/modal-prime.module';
import { AppContainerBulkService } from '@app/services/bulk-services/app.container.bulk.service';
import { GlobalFacade } from '@app/global.facade';
import { GlobalFilterPipesModule } from '@app/pipes/global-filters/global-filter-pipes.module';
import { NavigationService } from '@app/services/navigation.service';
import { SnackbarType } from '@design-system/components/molecules/snackbar/entities/snackbar.types';
import { AuthInterceptor } from '@app/services/auth-interceptor.service';
import { hydrationMetaReducer } from '@app/store/reducers/hydration/hydration.reducer';
import { DetachedRouteHandlesService } from '@app/services/detached-route-handles.service';
import { travelIndexedDB, travelSearchDB, travelSearchDBData } from '@shared/base-search-bar/constants/search-db.const';


const hydratedReducers: MetaReducer[] = [hydrationMetaReducer];
const metaReducers = ENV.production ? hydratedReducers : [...hydratedReducers, storeFreeze];

export const REDUCER_TOKEN = new InjectionToken('Registered Reducers');

registerLocaleData(getLocale());

declare module '@angular/core' {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  interface ModuleWithProviders<T = any> {
    ngModule: Type<T>;
    providers?: (Provider | EnvironmentProviders)[];
  }
}

const providers = [
  AppFacade,
  GlobalFacade,
  ModuleCountryGuard,
  MobileDeviceService,
  PropertiesAmplitudeService,
  TriggerAmplitudeService,
  AvoService,
  LocalStorageService,
  SessionStorageService,
  FrequentFlyerProgramsService,
  ProvincesAndCitiesExternalService,
  CookieService,
  AppContainerBulkService,
  NavigationService,
  DetachedRouteHandlesService,
  provideHttpClient(withInterceptorsFromDi()),
  {
    provide: REDUCER_TOKEN,
    useValue: globalReducers
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: ErrorInterceptoService,
    multi: true
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthInterceptor,
    multi: true
  },
  provideIndexedDb({
    name: travelIndexedDB,
    version: 1,
    objectStoresMeta: [
      {
        store: travelSearchDB,
        storeConfig: { keyPath: 'id', autoIncrement: false },
        storeSchema: [
          { name: travelSearchDBData, keypath: travelSearchDBData, options: { unique: true } },
        ]
      },
    ]
  }),
  provideClientHydration(withIncrementalHydration())
];

@NgModule({
  declarations: [
    AppContainer
  ],
  imports: [
    CommonModule,
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot({
      toastComponent: TravelToastComponent,
      preventDuplicates: true,
      iconClasses: {
        error: SnackbarType.Error,
        info: SnackbarType.Notification,
        success: SnackbarType.Success,
        warning: SnackbarType.Warning,
      }
    }),
    TravelToastModule,
    StoreModule.forRoot(REDUCER_TOKEN, {
      metaReducers: metaReducers
    }),
    CoreModule,
    EffectsModule.forRoot([
      BootstrapEffects,
      RouterEffect,
      ToastrEffect,
      LocalStorageEffect
    ]),
    NotificationsModule,
    StatsigModule,
    MaintenancePageModule,
    GlobalFilterPipesModule,
    ModalPrimeModule
  ],
  providers: providers,
  bootstrap: [AppContainer]
})
export class AppModule { }

